/** Entitlement display helpers for Settings → Subscription (#447). */

export type SubscriptionStatusPayload = {
  billing_interval?: string | null;
  current_period_end?: string | null;
  subscription_ends_at?: string | null;
  amount?: number | null;
  price_currency?: string | null;
  member_since?: string | null;
};

const CURRENCY_SYMBOLS: Record<string, string> = {
  USD: '$',
  CAD: 'CA$',
  GBP: '£',
  EUR: '€',
  AUD: 'A$',
  NZD: 'NZ$',
};

export function formatBillingPlanLabel(
  interval: string | null | undefined,
): 'Annual' | 'Monthly' | null {
  if (interval === 'year') return 'Annual';
  if (interval === 'month') return 'Monthly';
  return null;
}

export function formatBillingPeriodSuffix(
  interval: string | null | undefined,
): 'year' | 'month' | null {
  if (interval === 'year') return 'year';
  if (interval === 'month') return 'month';
  return null;
}

export function formatSubscriptionAmount(
  amountCents: number | null | undefined,
  currency: string | null | undefined,
): string | null {
  if (typeof amountCents !== 'number') return null;
  const sym = CURRENCY_SYMBOLS[currency || 'USD'] || '$';
  const decimals = amountCents % 100 === 0 ? 0 : 2;
  return `${sym}${(amountCents / 100).toFixed(decimals)}`;
}

export function formatSubscriptionPriceLine(
  sub: SubscriptionStatusPayload | null | undefined,
): string | null {
  const amount = formatSubscriptionAmount(sub?.amount, sub?.price_currency);
  const period = formatBillingPeriodSuffix(sub?.billing_interval);
  if (!amount || !period) return null;
  return `${amount}/${period}`;
}

/** Active subscribers renew at Stripe current_period_end, not subscription_ends_at. */
export function getActiveRenewalDateIso(
  sub: SubscriptionStatusPayload | null | undefined,
): string | null {
  return sub?.current_period_end || null;
}

export type EntitlementDetailRow = { label: string; value: string };

export function buildActiveEntitlementRows(
  sub: SubscriptionStatusPayload | null | undefined,
): EntitlementDetailRow[] {
  const rows: EntitlementDetailRow[] = [];
  const plan = formatBillingPlanLabel(sub?.billing_interval);
  if (plan) rows.push({ label: 'Plan', value: plan });
  const price = formatSubscriptionPriceLine(sub);
  if (price) rows.push({ label: 'Price', value: price });
  const renews = getActiveRenewalDateIso(sub);
  if (renews) rows.push({ label: 'Renews on', value: renews });
  return rows;
}

export function buildCancellingEntitlementRows(
  sub: SubscriptionStatusPayload | null | undefined,
): EntitlementDetailRow[] {
  const rows: EntitlementDetailRow[] = [];
  const plan = formatBillingPlanLabel(sub?.billing_interval);
  if (plan) rows.push({ label: 'Plan', value: plan });
  const price = formatSubscriptionPriceLine(sub);
  if (price) rows.push({ label: 'Price', value: price });
  return rows;
}
